"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { SectionHeader, StatsGrid } from "fansunited-sports-ui";
import { useTeamPageData } from "../context/TeamPageDataContext";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { AdaptedTeamEvent } from "@/utilities/data/team/types";
export interface TeamStatsProps {
  teamId: string;
}
function calculateStatsFromEvents(events: AdaptedTeamEvent[], teamId: string): {
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
} {
  let played = 0;
  let won = 0;
  let drawn = 0;
  let lost = 0;
  let goalsFor = 0;
  let goalsAgainst = 0;
  for (const event of events) {
    const isHome = event.competitorOne.id === teamId;
    const isAway = event.competitorTwo.id === teamId;
    if (!isHome && !isAway) continue;
    const statusType = event.status?.type?.toUpperCase();
    if (statusType !== "FINISHED") continue;
    played++;
    const homeScore = parseInt(event.score?.fullTime?.competitorOne || "0");
    const awayScore = parseInt(event.score?.fullTime?.competitorTwo || "0");
    const teamScore = isHome ? homeScore : awayScore;
    const opponentScore = isHome ? awayScore : homeScore;
    goalsFor += teamScore;
    goalsAgainst += opponentScore;
    if (teamScore > opponentScore) won++;else if (teamScore < opponentScore) lost++;else drawn++;
  }
  return {
    played,
    won,
    drawn,
    lost,
    goalsFor,
    goalsAgainst
  };
}

/**
 * Team's season stats grid.
 * Single-competition mode: uses standings data (includes position + points).
 * Multi-competition mode: aggregates from compact events.
 */
export const TeamStats: React.FC<TeamStatsProps> = t0 => {
  const $ = _c(89);
  const {
    teamId
  } = t0;
  const {
    events,
    teamStanding,
    isSingleCompetition
  } = useTeamPageData();
  const labels = useLabels().teamPage;
  let t1;
  bb0: {
    if (isSingleCompetition) {
      t1 = null;
      break bb0;
    }
    if (!events || events.length === 0) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== events || $[1] !== teamId) {
      t2 = calculateStatsFromEvents(events, teamId);
      $[0] = events;
      $[1] = teamId;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const calculatedStats = t1;
  const hasData = isSingleCompetition ? !!teamStanding : !!calculatedStats;
  if (!hasData) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        typography: {
          name: "text-sm uppercase font-semibold text-foreground"
        }
      };
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== labels.seasonStats.title) {
      t3 = {
        name: labels.seasonStats.title,
        styles: t2
      };
      $[4] = labels.seasonStats.title;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        container: "mb-2"
      };
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t3) {
      t5 = <SectionHeader entity={t3} styles={t4} />;
      $[7] = t3;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== labels.noStatsAvailable) {
      t6 = <div className="bg-card rounded-lg p-6"><p className="text-center text-muted-foreground">{labels.noStatsAvailable}</p></div>;
      $[9] = labels.noStatsAvailable;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== t5 || $[12] !== t6) {
      t7 = <div>{t5}{t6}</div>;
      $[11] = t5;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let statsData;
  if (isSingleCompetition && teamStanding) {
    const s = teamStanding.stats;
    const t2 = s.played ?? 0;
    let t3;
    if ($[14] !== labels.seasonStats.played || $[15] !== t2) {
      t3 = {
        id: "played",
        label: labels.seasonStats.played,
        value: t2
      };
      $[14] = labels.seasonStats.played;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    const t4 = s.won ?? 0;
    let t5;
    if ($[17] !== labels.seasonStats.won || $[18] !== t4) {
      t5 = {
        id: "won",
        label: labels.seasonStats.won,
        value: t4
      };
      $[17] = labels.seasonStats.won;
      $[18] = t4;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    const t6 = s.drawn ?? 0;
    let t7;
    if ($[20] !== labels.seasonStats.drawn || $[21] !== t6) {
      t7 = {
        id: "drawn",
        label: labels.seasonStats.drawn,
        value: t6
      };
      $[20] = labels.seasonStats.drawn;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    const t8 = s.lost ?? 0;
    let t9;
    if ($[23] !== labels.seasonStats.lost || $[24] !== t8) {
      t9 = {
        id: "lost",
        label: labels.seasonStats.lost,
        value: t8
      };
      $[23] = labels.seasonStats.lost;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== labels.seasonStats.tablePosition || $[27] !== teamStanding.rank) {
      t10 = {
        id: "position",
        label: labels.seasonStats.tablePosition,
        value: teamStanding.rank
      };
      $[26] = labels.seasonStats.tablePosition;
      $[27] = teamStanding.rank;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const t11 = s.points ?? 0;
    let t12;
    if ($[29] !== labels.seasonStats.points || $[30] !== t11) {
      t12 = {
        id: "points",
        label: labels.seasonStats.points,
        value: t11
      };
      $[29] = labels.seasonStats.points;
      $[30] = t11;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    const t13 = s.goalsFor ?? 0;
    let t14;
    if ($[32] !== labels.seasonStats.goalsScored || $[33] !== t13) {
      t14 = {
        id: "goalsFor",
        label: labels.seasonStats.goalsScored,
        value: t13
      };
      $[32] = labels.seasonStats.goalsScored;
      $[33] = t13;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    const t15 = s.goalsAgainst ?? 0;
    let t16;
    if ($[35] !== labels.seasonStats.goalsConceded || $[36] !== t15) {
      t16 = {
        id: "goalsAgainst",
        label: labels.seasonStats.goalsConceded,
        value: t15
      };
      $[35] = labels.seasonStats.goalsConceded;
      $[36] = t15;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== t10 || $[39] !== t12 || $[40] !== t14 || $[41] !== t16 || $[42] !== t3 || $[43] !== t5 || $[44] !== t7 || $[45] !== t9) {
      t17 = [t3, t5, t7, t9, t10, t12, t14, t16];
      $[38] = t10;
      $[39] = t12;
      $[40] = t14;
      $[41] = t16;
      $[42] = t3;
      $[43] = t5;
      $[44] = t7;
      $[45] = t9;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    statsData = t17;
  } else {
    if (calculatedStats) {
      const goalDifference = calculatedStats.goalsFor - calculatedStats.goalsAgainst;
      let t2;
      if ($[47] !== calculatedStats.played || $[48] !== labels.seasonStats.played) {
        t2 = {
          id: "played",
          label: labels.seasonStats.played,
          value: calculatedStats.played
        };
        $[47] = calculatedStats.played;
        $[48] = labels.seasonStats.played;
        $[49] = t2;
      } else {
        t2 = $[49];
      }
      let t3;
      if ($[50] !== calculatedStats.won || $[51] !== labels.seasonStats.won) {
        t3 = {
          id: "won",
          label: labels.seasonStats.won,
          value: calculatedStats.won
        };
        $[50] = calculatedStats.won;
        $[51] = labels.seasonStats.won;
        $[52] = t3;
      } else {
        t3 = $[52];
      }
      let t4;
      if ($[53] !== calculatedStats.drawn || $[54] !== labels.seasonStats.drawn) {
        t4 = {
          id: "drawn",
          label: labels.seasonStats.drawn,
          value: calculatedStats.drawn
        };
        $[53] = calculatedStats.drawn;
        $[54] = labels.seasonStats.drawn;
        $[55] = t4;
      } else {
        t4 = $[55];
      }
      let t5;
      if ($[56] !== calculatedStats.lost || $[57] !== labels.seasonStats.lost) {
        t5 = {
          id: "lost",
          label: labels.seasonStats.lost,
          value: calculatedStats.lost
        };
        $[56] = calculatedStats.lost;
        $[57] = labels.seasonStats.lost;
        $[58] = t5;
      } else {
        t5 = $[58];
      }
      let t6;
      if ($[59] !== calculatedStats.goalsFor || $[60] !== labels.seasonStats.goalsScored) {
        t6 = {
          id: "goalsFor",
          label: labels.seasonStats.goalsScored,
          value: calculatedStats.goalsFor
        };
        $[59] = calculatedStats.goalsFor;
        $[60] = labels.seasonStats.goalsScored;
        $[61] = t6;
      } else {
        t6 = $[61];
      }
      let t7;
      if ($[62] !== calculatedStats.goalsAgainst || $[63] !== labels.seasonStats.goalsConceded) {
        t7 = {
          id: "goalsAgainst",
          label: labels.seasonStats.goalsConceded,
          value: calculatedStats.goalsAgainst
        };
        $[62] = calculatedStats.goalsAgainst;
        $[63] = labels.seasonStats.goalsConceded;
        $[64] = t7;
      } else {
        t7 = $[64];
      }
      let t8;
      if ($[65] !== goalDifference || $[66] !== labels.seasonStats.goalDifference) {
        t8 = {
          id: "goalDifference",
          label: labels.seasonStats.goalDifference,
          value: goalDifference
        };
        $[65] = goalDifference;
        $[66] = labels.seasonStats.goalDifference;
        $[67] = t8;
      } else {
        t8 = $[67];
      }
      let t9;
      if ($[68] !== t2 || $[69] !== t3 || $[70] !== t4 || $[71] !== t5 || $[72] !== t6 || $[73] !== t7 || $[74] !== t8) {
        t9 = [t2, t3, t4, t5, t6, t7, t8];
        $[68] = t2;
        $[69] = t3;
        $[70] = t4;
        $[71] = t5;
        $[72] = t6;
        $[73] = t7;
        $[74] = t8;
        $[75] = t9;
      } else {
        t9 = $[75];
      }
      statsData = t9;
    } else {
      let t2;
      if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[76] = t2;
      } else {
        t2 = $[76];
      }
      statsData = t2;
    }
  }
  let t2;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      }
    };
    $[77] = t2;
  } else {
    t2 = $[77];
  }
  let t3;
  if ($[78] !== labels.seasonStats.title) {
    t3 = {
      name: labels.seasonStats.title,
      styles: t2
    };
    $[78] = labels.seasonStats.title;
    $[79] = t3;
  } else {
    t3 = $[79];
  }
  let t4;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      container: "mb-2"
    };
    $[80] = t4;
  } else {
    t4 = $[80];
  }
  let t5;
  if ($[81] !== t3) {
    t5 = <SectionHeader entity={t3} styles={t4} />;
    $[81] = t3;
    $[82] = t5;
  } else {
    t5 = $[82];
  }
  let t6;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      grid: {
        colsDesktop: 4,
        colsMobile: 2
      },
      item: {
        container: "bg-card shadow-md py-3.5"
      }
    };
    $[83] = t6;
  } else {
    t6 = $[83];
  }
  let t7;
  if ($[84] !== statsData) {
    t7 = <StatsGrid items={statsData} styles={t6} />;
    $[84] = statsData;
    $[85] = t7;
  } else {
    t7 = $[85];
  }
  let t8;
  if ($[86] !== t5 || $[87] !== t7) {
    t8 = <div>{t5}{t7}</div>;
    $[86] = t5;
    $[87] = t7;
    $[88] = t8;
  } else {
    t8 = $[88];
  }
  return t8;
};